import React, { useState } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Folder from 'lucide-react/dist/esm/icons/folder';
import Palette from 'lucide-react/dist/esm/icons/palette';
import Image from 'lucide-react/dist/esm/icons/image';
import { MyFolder, FolderCreateOptions } from '../../lib/myFilesService';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface FolderCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, options: FolderCreateOptions) => Promise<void>;
  currentPath: string;
}

const folderIcons = [
  { name: 'folder', icon: '📁' },
  { name: 'briefcase', icon: '💼' },
  { name: 'user', icon: '👤' },
  { name: 'image', icon: '🖼️' },
  { name: 'video', icon: '🎥' },
  { name: 'music', icon: '🎵' },
  { name: 'archive', icon: '📦' },
  { name: 'document', icon: '📄' },
  { name: 'heart', icon: '❤️' },
  { name: 'star', icon: '⭐' },
  { name: 'home', icon: '🏠' },
  { name: 'car', icon: '🚗' },
  { name: 'plane', icon: '✈️' },
  { name: 'gift', icon: '🎁' },
  { name: 'book', icon: '📚' },
  { name: 'graduation-cap', icon: '🎓' },
];

const folderColors = [
  '#abd3d2', // iDecide primary
  '#a0b4ae', // iDecide secondary
  '#172241', // iDecide accent
  '#4ade80', // Success green
  '#facc15', // Warning yellow
  '#f97316', // Orange
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#ef4444', // Red
  '#3b82f6', // Blue
];

export function FolderCreateModal({
  isOpen,
  onClose,
  onCreate,
  currentPath
}: FolderCreateModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState('#abd3d2');
  const [selectedIcon, setSelectedIcon] = useState('folder');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }

    if (name.length > 50) {
      setError('Folder name must be less than 50 characters');
      return;
    }

    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(name)) {
      setError('Folder name contains invalid characters');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const options: FolderCreateOptions = {
        description: description || undefined,
        color: selectedColor,
        icon: selectedIcon,
      };

      await onCreate(name.trim(), options);
      
      // Reset form
      setName('');
      setDescription('');
      setSelectedColor('#abd3d2');
      setSelectedIcon('folder');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setName('');
      setDescription('');
      setSelectedColor('#abd3d2');
      setSelectedIcon('folder');
      setError(null);
      onClose();
    }
  };

  const getCurrentPathDisplay = () => {
    if (currentPath === '/') return 'Root';
    return currentPath.slice(0, -1).split('/').pop() || 'Root';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-idecide-dark">Create New Folder</h2>
          <button
            onClick={handleClose}
            disabled={creating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Folder Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Folder Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={creating}
            placeholder="Enter folder name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent disabled:opacity-50"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">
            {name.length}/50 characters
          </p>
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={creating}
            placeholder="Enter folder description..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent disabled:opacity-50 resize-none"
          />
        </div>

        {/* Location */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
            <p className="text-sm text-gray-600">
              Inside: <span className="font-medium">{getCurrentPathDisplay()}</span>
            </p>
          </div>
        </div>

        {/* Folder Icon */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Folder Icon
          </label>
          <div className="grid grid-cols-8 gap-2">
            {folderIcons.map((icon) => (
              <button
                key={icon.name}
                onClick={() => setSelectedIcon(icon.name)}
                disabled={creating}
                className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-lg transition-all ${
                  selectedIcon === icon.name
                    ? 'border-idecide-primary bg-idecide-primary bg-opacity-10'
                    : 'border-gray-300 hover:border-idecide-primary hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {icon.icon}
              </button>
            ))}
          </div>
        </div>

        {/* Folder Color */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Folder Color
          </label>
          <div className="grid grid-cols-6 gap-2">
            {folderColors.map((color) => (
              <button
                key={color}
                onClick={() => setSelectedColor(color)}
                disabled={creating}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  selectedColor === color
                    ? 'border-gray-800 scale-110'
                    : 'border-gray-300 hover:border-gray-400'
                } disabled:opacity-50`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: selectedColor + '20' }}
            >
              <span className="text-lg">
                {folderIcons.find(icon => icon.name === selectedIcon)?.icon}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {name || 'New Folder'}
              </p>
              {description && (
                <p className="text-sm text-gray-500">{description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleClose}
            disabled={creating}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            className="flex-1 bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
          >
            {creating ? 'Creating...' : 'Create Folder'}
          </Button>
        </div>
      </Card>
    </div>
  );
} 